/** Angular */
import { Injectable, Injector } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Models */
import { DsOrganisationProject } from '../models/ds-organisation-project';
import { DsOrganisation } from '../models/ds-organisation';
import { BydModel } from '@sixense/byd-core-service';
import { DsPins } from '../models/ds-pins';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DSOrganisationProjectTemplate } from '../models/ds-organisation-project-template';
import { DsExportTemplateResponse } from '../models/ds-export-template-response.model';

@Injectable({
  providedIn: 'root',
})
export class DsOrganisationsService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Service constructor.
   *
   * @param sanitizer DomSanitizer.
   * @param injector creates a service injector instance
   */
  constructor(protected sanitizer: DomSanitizer, protected injector: Injector) {
    super(injector);
  }

  /**
   * Get a organisation.
   *
   * @param  organisationId Organisation ID
   * @returns Observable type `<DsOrganisation>`
   */
  public getOrganisation(organisationId: number): Observable<DsOrganisation> {
    this.path = `/organisations/${organisationId}`;
    return this.getModel<DsOrganisation>();
  }

  /**
   * Get all organisations.
   *
   * @returns Observable type `<BydModel<DsOrganisation[]>>`
   */
  public getAllOrganisations(): Observable<BydModel<DsOrganisation>> {
    this.path = '/organisations';
    return this.getModels<DsOrganisation>();
  }

  /**
   * Get all organisations linked of the user.
   *
   * @param  userId user ID
   * @returns Observable type `<BydModel<DsOrganisation>>`
   */
  public getOrganisationsLinksUser(userId: number): Observable<BydModel<DsOrganisation>> {
    this.path = `/organisations/users/${userId}`;
    return this.getModels<DsOrganisation>();
  }

  /**
   * Get project organisation infos.
   *
   * @param projectId ID of project
   * @returns Observable type `<BydModel<DsOrganisation>>`
   */
  public getProjectOrganisationInfos(projectId: number): Observable<DsOrganisation> {
    this.path = `/projects/${projectId}/organisations/infos`;
    return this.getModel<DsOrganisation>();
  }

  /**
   * Get organisation ID of the selected project.
   *
   * @param projectId ID of project
   * @returns Observavle type `<number>`
   */
  public getProjectOrganisation(projectId: number): Observable<number> {
    this.path = `/projects/${projectId}/organisations`;
    return this.get<number>();
  }

  /**
   * Get number of pins linked organisation.
   *
   * @param  organisationId Organisation ID
   * @returns Observable type `<DsPins>`
   */
  public getPinsLinksOrganisation(organisationId: number): Observable<DsPins> {
    this.path = `/organisations/${organisationId}/pinscount`;
    return this.get<DsPins>();
  }

  /**
   * Get number of projects linked organisation.
   *
   * @param  organisationId Organisation ID
   * @returns Observable type `<DsOrganisationProject>`
   */
  public getProjectsCountLinksOrganisation(organisationId: number): Observable<DsOrganisationProject> {
    this.path = `/organisations/${organisationId}/projectscount`;
    return this.get<DsOrganisationProject>();
  }

  /**
   * Add new organisation.
   *
   * @param  organisationName Organisation name
   * @param  parentId Organisation parent ID
   * @returns Observable type `<number>`
   */
  public addOrganisation(organisationName: string, parentId: number): Observable<number> {
    this.path = '/organisations';
    return this.post<number>({ body: { name: organisationName, parentId } });
  }

  /**
   * Update organisation.
   *
   * @param  organisationId Organisation ID
   * @param  organisationName Organisation name
   * @param  parentId Organisation parent ID
   * @returns Observable type `<boolean>`
   */
  public updateOrganisation(organisationId: number, organisationName: string, parentId: number): Observable<boolean> {
    this.path = `/organisations/${organisationId}`;
    return this.patch<boolean>({ body: { name: organisationName, parentId } });
  }

  /**
   * Remove a organisation.
   *
   * @param  organisationId Organisation ID
   * @returns Observable type `<boolean>`
   */
  public deleteOrganisation(organisationId: number): Observable<boolean> {
    this.path = `/organisations/${organisationId}`;
    return this.delete<boolean>();
  }

  /**
   * Add link user organisation.
   *
   * @param  organisationId Organisation ID
   * @param  userId User ID
   * @param  isDirector Check if the user is director
   * @returns Observable type `<boolean>`
   */
  public addLinkUserOrganisation(organisationId: number, userId: number, isDirector: boolean): Observable<boolean> {
    this.path = `/organisations/${organisationId}/users/${userId}`;
    return this.post<boolean>({ body: { isDirector } });
  }

  /**
   * Remove link user organisation.
   *
   * @param  organisationId Organisation ID
   * @param  userId User ID
   * @returns Observable type `<boolean>`
   */
  public removeLinkUserOrganisation(organisationId: number, userId: number): Observable<boolean> {
    this.path = `/organisations/${organisationId}/users/${userId}`;
    return this.delete<boolean>();
  }

  /**
   * Move form template model from category.
   *
   * @param  organisationId Organisation ID
   * @param  categoryId Catgeory ID
   * @param  formId Form ID
   * @returns Observable type `<boolean>`
   */
  public moveFormTemplateModelToCategory(
    organisationId: number,
    categoryId: number,
    formId: number
  ): Observable<boolean> {
    this.path = `/organisations/${organisationId}/categories/${categoryId}/forms/${formId}`;
    return this.post<boolean>();
  }

  /**
   * Add a project to an organisation.
   *
   * @param organisationId Organisation ID
   * @param projectId Project ID
   * @returns Observable type `<DsOrganisation>`
   */
  public addProjectToOrganisation(organisationId: number, projectId: number): Observable<DsOrganisation> {
    this.path = `/organisations/${organisationId}/projects/${projectId}`;
    return this.postModel<DsOrganisation>();
  }

  /**
   * Remove the link between the project and the organisation.
   *
   * @param organisationId Organisation ID
   * @param projectId Project ID
   * @returns Observable type `<DsOrganisation>`
   */
  public deleteProjectFromOrganisation(organisationId: number, projectId: number): Observable<DsOrganisation> {
    this.path = `/organisations/${organisationId}/projects/${projectId}`;
    return this.delete<DsOrganisation>();
  }

  /**
   * Get Project template by organisationId.
   *
   * @param organisationId Organisation ID.
   * @returns Observable of json
   */
  public exportProjectTemplate(organisationId: number): Observable<DsExportTemplateResponse> {
    this.path = `/dsorga/v1/organisations/${organisationId}/projecttemplates`;
    return this.get<DsExportTemplateResponse>();
  }

  /**
   * Change Project template by organisationId.
   *
   * @param organisationId Organisation ID.
   * @param projectTemplate JsonObject project template.
   * @returns Observable of json
   */
  public importProjectTemplate(
    organisationId: number,
    projectTemplate: DSOrganisationProjectTemplate
  ): Observable<DsOrganisation> {
    this.path = `/dsorga/v1/organisations/${organisationId}/projecttemplates`;
    return this.postModel<DsOrganisation>({ body: projectTemplate });
  }

  /**
   * Generate File URI from JSON object.
   *
   * @param json JsonObject
   * @returns string URI
   */
  public generateFileURIFromJSONObject(json): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `data:text/json;charset=UTF-8,${encodeURIComponent(JSON.stringify(json))}`
    );
  }

  /**
   * Link a project to an organisation.
   *
   * @param organisationId project id
   * @param projectId organisation id
   * @returns Observable<DsProject>
   */
  public linkProject(organisationId: number, projectId: number): Observable<boolean> {
    this.path = `/organisations/${organisationId}/projects/${projectId}`;
    return this.post();
  }

  /**
   * Create DsOrganisation instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsOrganisation` instance.
   */
  public createNewInstance(jsonObject: DsOrganisation): DsOrganisation {
    return new DsOrganisation(jsonObject);
  }
}
